// src/pages/FirebaseTest.jsx
import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { clientService } from '../services/clientService';
import { appointmentService } from '../services/appointmentService';
import { dataService } from '../services/dataService';
import FirebaseLogin from '../components/auth/FirebaseLogin';
import { toast } from 'react-hot-toast';
import { FiCheck, FiX, FiDatabase, FiUser, FiCalendar, FiSettings } from 'react-icons/fi';

export default function FirebaseTest() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testResults, setTestResults] = useState({});
  const [dataStats, setDataStats] = useState({});

  useEffect(() => {
    // Listen for authentication state changes
    const unsubscribe = authService.onAuthStateChanged((user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      loadDataStats();
    }
  }, [user]);

  const loadDataStats = async () => {
    try {
      const [clients, appointments] = await Promise.all([
        clientService.getAll(),
        appointmentService.getAll()
      ]);

      setDataStats({
        mode: dataService.getCurrentMode(),
        clients: clients.length,
        appointments: appointments.length,
        totalRecords: clients.length + appointments.length
      });
    } catch (error) {
      console.error('Error loading data stats:', error);
    }
  };

  const runFirebaseTests = async () => {
    setTestResults({});
    toast.loading('Running Firebase tests...');

    const results = {};

    // Test 1: Firebase Connection
    try {
      await dataService.testConnection();
      results.connection = { success: true, message: 'Firebase connection successful' };
    } catch (error) {
      results.connection = { success: false, message: error.message };
    }

    // Test 2: Create Test Client
    try {
      const testClient = await clientService.create({
        name: 'Firebase Test Client',
        email: 'test@firebase.com',
        phone: '123-456-7890'
      });
      results.createClient = { success: true, message: 'Client created successfully', data: testClient };
    } catch (error) {
      results.createClient = { success: false, message: error.message };
    }

    // Test 3: Read Clients
    try {
      const clients = await clientService.getAll();
      results.readClients = { success: true, message: `Retrieved ${clients.length} clients` };
    } catch (error) {
      results.readClients = { success: false, message: error.message };
    }

    // Test 4: Create Test Appointment
    try {
      const testAppointment = await appointmentService.create({
        clientId: 'test-client',
        client: { name: 'Test Client' },
        date: '2024-01-15',
        time: '10:00',
        duration: 60,
        type: 'Firebase Test',
        status: 'upcoming'
      });
      results.createAppointment = { success: true, message: 'Appointment created successfully', data: testAppointment };
    } catch (error) {
      results.createAppointment = { success: false, message: error.message };
    }

    setTestResults(results);
    toast.dismiss();

    const successCount = Object.values(results).filter(r => r.success).length;
    const totalTests = Object.keys(results).length;

    if (successCount === totalTests) {
      toast.success(`All ${totalTests} tests passed! 🎉`);
    } else {
      toast.error(`${successCount}/${totalTests} tests passed`);
    }

    // Reload stats after tests
    await loadDataStats();
  };

  const handleLogout = async () => {
    const result = await authService.signOut();
    if (result.success) {
      toast.success('Signed out successfully');
      setUser(null);
    } else {
      toast.error('Logout failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <FirebaseLogin onLoginSuccess={setUser} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Firebase Test Dashboard</h1>
              <p className="text-gray-600">Welcome, {user.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Data Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3">
              <FiDatabase className="text-blue-600 text-xl" />
              <div>
                <p className="text-sm text-gray-600">Storage Mode</p>
                <p className="text-lg font-semibold">{dataStats.mode || 'Loading...'}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3">
              <FiUser className="text-green-600 text-xl" />
              <div>
                <p className="text-sm text-gray-600">Clients</p>
                <p className="text-lg font-semibold">{dataStats.clients || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3">
              <FiCalendar className="text-purple-600 text-xl" />
              <div>
                <p className="text-sm text-gray-600">Appointments</p>
                <p className="text-lg font-semibold">{dataStats.appointments || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3">
              <FiSettings className="text-orange-600 text-xl" />
              <div>
                <p className="text-sm text-gray-600">Total Records</p>
                <p className="text-lg font-semibold">{dataStats.totalRecords || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Test Controls */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Firebase Tests</h2>
            <button
              onClick={runFirebaseTests}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Run Tests
            </button>
          </div>

          {Object.keys(testResults).length > 0 && (
            <div className="space-y-3">
              {Object.entries(testResults).map(([testName, result]) => (
                <div key={testName} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  {result.success ? (
                    <FiCheck className="text-green-600 text-xl" />
                  ) : (
                    <FiX className="text-red-600 text-xl" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium capitalize">{testName.replace(/([A-Z])/g, ' $1')}</p>
                    <p className="text-sm text-gray-600">{result.message}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-4">Next Steps</h2>
          <div className="space-y-3 text-gray-700">
            <p>✅ <strong>Firebase Authentication:</strong> Working! You're signed in as {user.email}</p>
            <p>✅ <strong>Firestore Database:</strong> Ready for testing</p>
            <p>🔄 <strong>Data Mode:</strong> Currently using {dataStats.mode} storage</p>
            <p>🧪 <strong>Testing:</strong> Click "Run Tests" to verify all Firebase operations</p>
          </div>
        </div>
      </div>
    </div>
  );
}
